import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import ImageUpload from '@/components/ImageUpload';

export default function AdminAboutContent() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({
    hero_title: '',
    hero_description: '',
    hero_image_url: '',
    mission_title: '',
    mission_description: '',
    vision_title: '',
    vision_description: ''
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await apiClient.get('/about-content');
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching about content:', error);
      toast.error('Failed to load about content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.put('/admin/about-content', content);
      toast.success('About content saved successfully!');
    } catch (error) {
      toast.error('Failed to save about content');
      console.error('Error saving content:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">About Section Content</h1>
            <p className="text-gray-600 mt-1">Manage the "About Us" section on homepage</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <form onSubmit={handleSave} className="bg-white rounded-lg shadow-md p-6 space-y-6">
          
          {/* Hero Section */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Hero Section (Homepage)</h2>
            
            <div className="space-y-4">
              <div>
                <Label>Hero Image (PNG recommended)</Label>
                <p className="text-sm text-gray-500 mb-2">Upload an image for the About section on homepage</p>
                <ImageUpload
                  value={content.hero_image_url}
                  onChange={(url) => setContent({ ...content, hero_image_url: url })}
                  label="About Section Image"
                />
              </div>

              <div>
                <Label>Hero Title</Label>
                <Input
                  value={content.hero_title}
                  onChange={(e) => setContent({ ...content, hero_title: e.target.value })}
                  placeholder="A Hub for Thinkers, Visionaries and Leaders"
                />
              </div>

              <div>
                <Label>Hero Description</Label>
                <Textarea
                  value={content.hero_description}
                  onChange={(e) => setContent({ ...content, hero_description: e.target.value })}
                  placeholder="The Circuit is where ambition meets momentum..."
                  rows={8}
                />
              </div>
            </div>
          </div>

          {/* Mission Section */}
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Mission (Optional)</h2>
            
            <div className="space-y-4">
              <div>
                <Label>Mission Title</Label>
                <Input
                  value={content.mission_title}
                  onChange={(e) => setContent({ ...content, mission_title: e.target.value })}
                  placeholder="Our Mission"
                />
              </div>

              <div>
                <Label>Mission Description</Label>
                <Textarea
                  value={content.mission_description}
                  onChange={(e) => setContent({ ...content, mission_description: e.target.value })}
                  placeholder="Our mission statement..."
                  rows={4}
                />
              </div>
            </div>
          </div>

          {/* Vision Section */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Vision (Optional)</h2>
            
            <div className="space-y-4">
              <div>
                <Label>Vision Title</Label>
                <Input
                  value={content.vision_title}
                  onChange={(e) => setContent({ ...content, vision_title: e.target.value })}
                  placeholder="Our Vision"
                />
              </div>

              <div>
                <Label>Vision Description</Label>
                <Textarea
                  value={content.vision_description}
                  onChange={(e) => setContent({ ...content, vision_description: e.target.value })}
                  placeholder="Our vision statement..."
                  rows={4}
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button
              type="submit"
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save size={18} className="mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
